// frontend/src/components/DashboardVendas.js
// =============================================
// DASHBOARD DE VENDAS — Filtro total por ano
// ✅ Todos os cards, charts e tabelas respeitam o ano
// ✅ Evolução mostra Jan → Dez do ANO escolhido
// ✅ Se não há dados → mostra "Sem dados para {ano}"
// ✅ Pie chart mostra apenas percentagens (nomes vão na legenda)
// ✅ PERSONALIZAÇÃO DE MOEDA E FORMATACAO DINAMICA
// 🔥 BLOCO 5: COMPRAS A FORNECEDOR integradas
// 🔥 Contabilidade: IVA Liquidado, Dedutível, a Pagar, a Receber
// =============================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
    BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, Area, AreaChart
} from 'recharts';
import {
    FaFileInvoiceDollar, FaMoneyBillWave, FaChartLine,
    FaUsers, FaExclamationTriangle, FaSync, FaBalanceScale,
    FaTruck, FaCalculator, FaArrowDown, FaArrowUp
} from 'react-icons/fa';
import './DashboardVendas.css';

const API = '/api' || 'http://localhost:3000';

const mesesOpcoes = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

// =============================================
// CONFIGURACAO PADRAO (fallback)
// =============================================
const CONFIG_PADRAO = {
    moeda: {
        simbolo: 'MT',
        codigo: 'MZN',
        casasDecimais: 2,
        separadorDecimal: ',',
        separadorMilhar: '.',
        posicaoSimbolo: 'depois',
        nome: 'Metical'
    },
    formatacao: {
        data: { formato: 'DD/MM/YYYY', separador: '/', incluirHora: false },
        numero: { separadorDecimal: ',', separadorMilhar: '.', casasDecimais: 2 }
    }
};

// =============================================
// CARD KPI
// =============================================
function KpiCard({ titulo, valor, sub, icone, cor, loading, semDados }) {
    return (
        <div className="kpi-card" style={{ borderTopColor: cor }}>
            <div className="kpi-icon" style={{ background: cor + '20', color: cor }}>
                {icone}
            </div>
            <div className="kpi-body">
                <span className="kpi-titulo">{titulo}</span>
                <span className="kpi-valor" style={{ opacity: semDados ? 0.45 : 1 }}>
                    {loading ? '...' : (semDados ? '—' : valor)}
                </span>
                {sub && !semDados && <span className="kpi-sub">{sub}</span>}
                {semDados && <span className="kpi-sub">sem dados</span>}
            </div>
        </div>
    );
}

// =============================================
// TOOLTIP CUSTOMIZADA
// =============================================
function CustomTooltip({ active, payload, label, formatValue }) {
    if (!active || !payload || !payload.length) return null;
    return (
        <div className="custom-tooltip">
            {label && <div className="custom-tooltip-label">{label}</div>}
            {payload.map((entry, i) => (
                <div key={i} className="custom-tooltip-item">
                    <span
                        className="custom-tooltip-dot"
                        style={{ background: entry.color || entry.payload?.cor || '#4f46e5' }}
                    />
                    <span className="custom-tooltip-name">{entry.name}:</span>
                    <span className="custom-tooltip-value">
                        {formatValue ? formatValue(entry.value) : entry.value}
                    </span>
                </div>
            ))}
        </div>
    );
}

// =============================================
// COMPONENTE PRINCIPAL
// =============================================
export default function DashboardVendas() {
    const [periodo, setPeriodo] = useState('mes');
    const [ano, setAno] = useState(new Date().getFullYear());
    const [mes, setMes] = useState(new Date().getMonth());
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState(null);
    const [dados, setDados] = useState(null);
    const [alertas, setAlertas] = useState([]);
    const [classificacoes, setClassificacoes] = useState([]);
    const [mostrarClassificacoes, setMostrarClassificacoes] = useState(false);

    // 🔥 Estado para configuracoes da empresa (moeda e formatacao)
    const [empresaConfig, setEmpresaConfig] = useState(CONFIG_PADRAO);

    const token = localStorage.getItem('token');
    const headers = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token]
    );

    // =============================================
    // 🔥 FORMATAR MOEDA (DINAMICA)
    // =============================================
    const formatMZN = useMemo(() => {
        return (valor) => {
            const cfg = empresaConfig.moeda || CONFIG_PADRAO.moeda;
            const sepDec = cfg.separadorDecimal || ',';
            const sepMil = cfg.separadorMilhar || '.';
            const casasDec = cfg.casasDecimais ?? 2;
            const simbolo = cfg.simbolo || 'MT';

            if (valor === undefined || valor === null || isNaN(valor)) {
                const zeros = '0'.repeat(casasDec);
                return `0${sepDec}${zeros} ${simbolo}`;
            }

            let valorFormatado = Number(valor).toFixed(casasDec);
            valorFormatado = valorFormatado.replace('.', sepDec);

            const partes = valorFormatado.split(sepDec);
            partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, sepMil);
            valorFormatado = partes.join(sepDec);

            return `${valorFormatado} ${simbolo}`;
        };
    }, [empresaConfig.moeda]);

    // =============================================
    // 🔥 FORMATAR NUMEROS (DINAMICA)
    // =============================================
    const formatNum = useMemo(() => {
        return (valor) => {
            const cfg = empresaConfig.formatacao?.numero || empresaConfig.moeda || CONFIG_PADRAO.formatacao.numero;
            const sepDec = cfg.separadorDecimal || ',';
            const sepMil = cfg.separadorMilhar || '.';
            const casasDec = cfg.casasDecimais !== undefined ? cfg.casasDecimais : 0;

            if (valor === undefined || valor === null || isNaN(valor)) {
                return '0';
            }

            let valorFormatado = Number(valor).toFixed(casasDec);
            valorFormatado = valorFormatado.replace('.', sepDec);

            const partes = valorFormatado.split(sepDec);
            partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, sepMil);
            valorFormatado = partes.join(sepDec);

            return valorFormatado;
        };
    }, [empresaConfig.formatacao, empresaConfig.moeda]);

    // =============================================
    // 🔥 FORMATAR DATA (DINAMICA)
    // =============================================
    const formatarData = useCallback((data) => {
        if (!data) return '-';
        const d = new Date(data);
        if (isNaN(d.getTime())) return '-';

        const cfg = empresaConfig.formatacao?.data || CONFIG_PADRAO.formatacao.data;
        let formato = cfg.formato || 'DD/MM/YYYY';
        const dia = String(d.getDate()).padStart(2, '0');
        const mesNum = String(d.getMonth() + 1).padStart(2, '0');
        const anoNum = d.getFullYear();

        formato = formato.replace('DD', dia);
        formato = formato.replace('MM', mesNum);
        formato = formato.replace('YYYY', anoNum);
        formato = formato.replace('YY', String(anoNum).slice(-2));

        if (cfg.incluirHora) {
            const horas = String(d.getHours()).padStart(2, '0');
            const minutos = String(d.getMinutes()).padStart(2, '0');
            return `${formato} ${horas}:${minutos}`;
        }

        return formato;
    }, [empresaConfig.formatacao]);

    // =============================================
    // 🔥 CARREGAR DADOS — uma unica chamada ao /completo
    // =============================================
    const carregar = useCallback(async () => {
        setLoading(true);
        setErro(null);
        try {
            const [resDash, resAlertas] = await Promise.all([
                axios.get(`${API}/dashboard-vendas/completo`, {
                    params: { periodo, ano, mes },
                    headers
                }),
                axios.get(`${API}/dashboard-vendas/alertas`, { headers })
            ]);

            if (resDash.data.success) {
                setDados(resDash.data);

                // 🔥 Atualizar moeda/formatacao com a resposta do backend
                if (resDash.data.configuracao) {
                    setEmpresaConfig(prev => ({
                        ...prev,
                        moeda: resDash.data.configuracao.moeda || prev.moeda,
                        formatacao: resDash.data.configuracao.formatacao || prev.formatacao
                    }));
                }
            }
            if (resAlertas.data.success) setAlertas(resAlertas.data.alertas);
        } catch (e) {
            console.error('[DashboardVendas]', e);
            setErro(e.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, [periodo, ano, mes, headers]);

    useEffect(() => { carregar(); }, [carregar]);

    // =============================================
    // CLASSIFICACAO DE CLIENTES
    // =============================================
    const carregarClassificacoes = async () => {
        try {
            const res = await axios.get(
                `${API}/dashboard-vendas/classificacao-clientes`,
                { headers }
            );
            if (res.data.success) {
                setClassificacoes(res.data.clientes);
                setMostrarClassificacoes(true);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const aplicarNivel = async (clienteId, nivel) => {
        try {
            await axios.post(
                `${API}/dashboard-vendas/aplicar-nivel`,
                { clienteId, nivel },
                { headers }
            );
            setClassificacoes(prev =>
                prev.map(c => c._id === clienteId
                    ? { ...c, nivelAtual: nivel, divergente: false }
                    : c)
            );
        } catch (e) {
            console.error(e);
        }
    };

    // ===== ESTADOS DE CARREGAMENTO / ERRO =====
    if (loading && !dados) {
        return (
            <div className="dashvendas-loading">
                <div className="spinner" />
                <p>A carregar dashboard...</p>
            </div>
        );
    }

    if (erro && !dados) {
        return (
            <div className="dashvendas-erro">
                <FaExclamationTriangle />
                <p>{erro}</p>
                <button onClick={carregar}>Tentar novamente</button>
            </div>
        );
    }

    // ===== DADOS =====
    const k = dados?.kpis || {};
    const funil = dados?.funil || [];
    const evolucao = dados?.evolucao || [];
    const topClientes = dados?.topClientes || [];
    const topProdutos = dados?.topProdutos || [];
    const metodos = dados?.metodosPagamento || [];
    const contab = dados?.contabilidade || { ivaPorTaxa: [] };
    const comprasContab = contab?.compras || {};

    // 🔥 Saldo = Recebido − Faturado
    const saldo = (Number(k.totalRecebido) || 0) - (Number(k.totalFaturado) || 0);

    // 🔥 IVA a pagar / a receber (vem do backend, mas com fallback)
    const ivaAPagar = contab.ivaAPagar ?? k.ivaAPagar ?? 0;
    const ivaAReceber = contab.ivaAReceber ?? (ivaAPagar < 0 ? Math.abs(ivaAPagar) : 0);

    // 🔥 Flags de "sem dados" — uma por secao
    const temDadosFunil = funil.some(f => (f.valor || 0) > 0);
    const temDadosEvolucao = evolucao.some(e =>
        (e.faturado || 0) > 0 || (e.recebido || 0) > 0 || (e.compras || 0) > 0
    );
    const temDadosTopClientes = topClientes.length > 0;
    const temDadosTopProdutos = topProdutos.length > 0;
    const temDadosMetodos = metodos.length > 0;
    const temDadosContab = (contab.ivaPorTaxa?.length || 0) > 0;
    const temDadosCompras = (k.numeroCompras || 0) > 0;

    return (
        <div className="dashvendas">

            {/* ===== HEADER ===== */}
            <header className="dashvendas-header">
                <div>
                    <h1>📊 Dashboard de Vendas</h1>
                    <p className="dashvendas-sub">
                        CRM • Vendas • Contabilidade • Compras • <strong>Ano {ano}</strong>
                    </p>
                </div>

                <div className="dashvendas-filtros">
                    <select value={periodo} onChange={e => setPeriodo(e.target.value)}>
                        <option value="mes">Mês</option>
                        <option value="trimestre">Trimestre</option>
                        <option value="ano">Ano</option>
                    </select>

                    {periodo === 'mes' && (
                        <select value={mes} onChange={e => setMes(parseInt(e.target.value))}>
                            {mesesOpcoes.map((m, i) => (
                                <option key={i} value={i}>{m}</option>
                            ))}
                        </select>
                    )}

                    <select value={ano} onChange={e => setAno(parseInt(e.target.value))}>
                        {[2023, 2024, 2025, 2026, 2027].map(a => (
                            <option key={a} value={a}>{a}</option>
                        ))}
                    </select>

                    <button className="btn-icon" onClick={carregar} title="Atualizar">
                        <FaSync />
                    </button>
                </div>
            </header>

            {/* ===== ALERTAS ===== */}
            {alertas.length > 0 && (
                <section className="dashvendas-alertas">
                    {alertas.map((a, i) => (
                        <div key={i} className={`alerta alerta-${a.tipo}`}>
                            <FaExclamationTriangle />
                            <div>
                                <strong>{a.titulo}</strong>
                                <span>{a.descricao}</span>
                            </div>
                        </div>
                    ))}
                </section>
            )}

            {/* ===== KPIs — VENDAS ===== */}
            <section className="dashvendas-kpis">
                <KpiCard
                    titulo="Total Faturado"
                    valor={formatMZN(k.totalFaturado)}
                    sub={`${formatNum(k.numeroFaturas)} faturas`}
                    icone={<FaFileInvoiceDollar />}
                    cor="#3b82f6"
                    loading={loading}
                    semDados={!k.numeroFaturas}
                />
                <KpiCard
                    titulo="Total Recebido"
                    valor={formatMZN(k.totalRecebido)}
                    sub={`${formatNum(k.numeroRecebimentos)} recebimentos`}
                    icone={<FaMoneyBillWave />}
                    cor="#10b981"
                    loading={loading}
                    semDados={!k.numeroRecebimentos}
                />
                <KpiCard
                    titulo="A Receber"
                    valor={formatMZN(k.aReceber)}
                    sub="Saldo em aberto"
                    icone={<FaChartLine />}
                    cor="#f59e0b"
                    loading={loading}
                    semDados={!k.numeroFaturas}
                />
                <KpiCard
                    titulo="Taxa de Conversão"
                    valor={`${k.taxaConversao || 0}%`}
                    sub={`${k.cotacoesConvertidas || 0}/${k.cotacoesCriadas || 0} cotações`}
                    icone={<FaUsers />}
                    cor="#8b5cf6"
                    loading={loading}
                    semDados={!k.cotacoesCriadas}
                />
                <KpiCard
                    titulo="Saldo"
                    valor={formatMZN(saldo)}
                    sub={saldo >= 0 ? 'saldo positivo' : 'saldo negativo'}
                    icone={<FaBalanceScale />}
                    cor={saldo >= 0 ? '#06b6d4' : '#dc2626'}
                    loading={loading}
                    semDados={!k.numeroFaturas && !k.numeroRecebimentos}
                />
            </section>

            {/* ===== KPIs — COMPRAS & IVA (NOVO) ===== */}
            <section className="dashvendas-kpis">
                <KpiCard
                    titulo="Total Compras"
                    valor={formatMZN(k.totalCompras)}
                    sub={`${formatNum(k.numeroCompras)} compras`}
                    icone={<FaTruck />}
                    cor="#0ea5e9"
                    loading={loading}
                    semDados={!k.numeroCompras}
                />
                <KpiCard
                    titulo="Pago a Fornecedores"
                    valor={formatMZN(k.totalPagoCompras)}
                    sub="Total liquidado"
                    icone={<FaArrowUp />}
                    cor="#14b8a6"
                    loading={loading}
                    semDados={!k.numeroCompras}
                />
                <KpiCard
                    titulo="A Pagar a Fornecedores"
                    valor={formatMZN(k.totalAPagar)}
                    sub="Saldo em aberto"
                    icone={<FaArrowDown />}
                    cor="#f97316"
                    loading={loading}
                    semDados={!k.numeroCompras}
                />
                <KpiCard
                    titulo="IVA Dedutível (Compras)"
                    valor={formatMZN(k.totalIvaDedutivel)}
                    sub="IVA suportado"
                    icone={<FaCalculator />}
                    cor="#a855f7"
                    loading={loading}
                    semDados={!k.numeroCompras}
                />
                <KpiCard
                    titulo={ivaAPagar >= 0 ? 'IVA a Pagar' : 'IVA a Recuperar'}
                    valor={formatMZN(Math.abs(ivaAPagar))}
                    sub={ivaAPagar >= 0 ? 'liquidado − dedutível' : 'credito de IVA'}
                    icone={<FaBalanceScale />}
                    cor={ivaAPagar >= 0 ? '#dc2626' : '#16a34a'}
                    loading={loading}
                    semDados={!k.numeroFaturas && !k.numeroCompras}
                />
            </section>

            {/* ===== FUNIL + EVOLUCAO ===== */}
            <section className="dashvendas-grid-2">
                <div className="dash-card">
                    <h3>🔻 Funil de Vendas ({ano})</h3>
                    <div className="chart-container bar-chart">
                        {!temDadosFunil ? (
                            <p className="vazio">Sem dados para {ano}</p>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={funil}
                                    layout="vertical"
                                    margin={{ top: 10, right: 30, bottom: 10, left: 10 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                    <XAxis
                                        type="number"
                                        allowDecimals={false}
                                        tick={{ fontSize: 11 }}
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <YAxis
                                        dataKey="etapa"
                                        type="category"
                                        width={130}
                                        tick={{ fontSize: 11 }}
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <Tooltip
                                        cursor={{ fill: 'rgba(79, 70, 229, 0.05)' }}
                                        content={<CustomTooltip />}
                                    />
                                    <Bar dataKey="valor" radius={[0, 8, 8, 0]} barSize={22}>
                                        {funil.map((f, i) => (
                                            <Cell key={i} fill={f.cor} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                <div className="dash-card">
                    <h3>📈 Evolução ({ano})</h3>
                    <div className="chart-container area-chart">
                        {!temDadosEvolucao ? (
                            <p className="vazio">Sem dados para {ano}</p>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart
                                    data={evolucao}
                                    margin={{ top: 10, right: 20, bottom: 10, left: 0 }}
                                >
                                    <defs>
                                        <linearGradient id="gradFat" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.7} />
                                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05} />
                                        </linearGradient>
                                        <linearGradient id="gradRec" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.7} />
                                            <stop offset="95%" stopColor="#10b981" stopOpacity={0.05} />
                                        </linearGradient>
                                        {/* 🔥 NOVO: gradient para compras */}
                                        <linearGradient id="gradCmp" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#f97316" stopOpacity={0.7} />
                                            <stop offset="95%" stopColor="#f97316" stopOpacity={0.05} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis
                                        dataKey="mes"
                                        tick={{ fontSize: 11 }}
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <YAxis
                                        tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                                        tick={{ fontSize: 11 }}
                                        tickLine={false}
                                        axisLine={false}
                                        width={45}
                                    />
                                    <Tooltip content={<CustomTooltip formatValue={formatMZN} />} />
                                    <Legend
                                        iconType="circle"
                                        iconSize={8}
                                        wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="faturado"
                                        stroke="#3b82f6"
                                        fill="url(#gradFat)"
                                        name="Faturado"
                                        strokeWidth={2}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="recebido"
                                        stroke="#10b981"
                                        fill="url(#gradRec)"
                                        name="Recebido"
                                        strokeWidth={2}
                                    />
                                    {/* 🔥 NOVO: linha de compras */}
                                    <Area
                                        type="monotone"
                                        dataKey="compras"
                                        stroke="#f97316"
                                        fill="url(#gradCmp)"
                                        name="Compras"
                                        strokeWidth={2}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>
            </section>

            {/* ===== TOP CLIENTES + TOP PRODUTOS ===== */}
            <section className="dashvendas-grid-2">
                <div className="dash-card">
                    <h3>🏆 Top Clientes ({ano})</h3>
                    <div className="tabela-wrapper">
                        <table className="tabela-dash">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Cliente</th>
                                    <th>Nível</th>
                                    <th>Faturado</th>
                                    <th>Faturas</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!temDadosTopClientes && (
                                    <tr><td colSpan="5" className="vazio">Sem dados para {ano}</td></tr>
                                )}
                                {topClientes.map((c, i) => (
                                    <tr key={c._id || i}>
                                        <td><strong>{i + 1}</strong></td>
                                        <td>{c.nome || '—'}</td>
                                        <td>
                                            <span className={`badge badge-${(c.nivelFidelidade || '').toLowerCase()}`}>
                                                {c.nivelFidelidade || '—'}
                                            </span>
                                        </td>
                                        <td>{formatMZN(c.totalFaturado)}</td>
                                        <td>{c.numeroFaturas}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="dash-card">
                    <h3>📦 Top Produtos/Serviços ({ano})</h3>
                    <div className="tabela-wrapper">
                        <table className="tabela-dash">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Item</th>
                                    <th>Tipo</th>
                                    <th>Qtd</th>
                                    <th>Receita</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!temDadosTopProdutos && (
                                    <tr><td colSpan="5" className="vazio">Sem dados para {ano}</td></tr>
                                )}
                                {topProdutos.map((p, i) => (
                                    <tr key={i}>
                                        <td><strong>{i + 1}</strong></td>
                                        <td>{p._id?.nome || '—'}</td>
                                        <td>{p._id?.tipo || '—'}</td>
                                        <td>{formatNum(p.quantidadeVendida)}</td>
                                        <td>{formatMZN(p.receita)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* ===== METODOS PAGAMENTO + CONTABILIDADE ===== */}
            <section className="dashvendas-grid-2">
                <div className="dash-card">
                    <h3>💳 Métodos de Pagamento ({ano})</h3>
                    {!temDadosMetodos ? (
                        <p className="vazio">Sem recibos para {ano}</p>
                    ) : (
                        <div className="pie-chart-container">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
                                    <Pie
                                        data={metodos}
                                        dataKey="total"
                                        nameKey="metodo"
                                        cx="50%"
                                        cy="45%"
                                        outerRadius="70%"
                                        innerRadius="40%"
                                        paddingAngle={2}
                                        labelLine={{
                                            stroke: 'var(--dv-chart-grid)',
                                            strokeWidth: 1
                                        }}
                                        label={({ percent, x, y, textAnchor }) => {
                                            if (percent < 0.05) return null;
                                            return (
                                                <text
                                                    x={x}
                                                    y={y}
                                                    textAnchor={textAnchor}
                                                    dominantBaseline="central"
                                                    className="recharts-pie-label-text"
                                                >
                                                    {`${(percent * 100).toFixed(0)}%`}
                                                </text>
                                            );
                                        }}
                                    >
                                        {metodos.map((m, i) => (
                                            <Cell
                                                key={i}
                                                fill={m.cor}
                                                stroke="var(--dv-chart-pie-stroke)"
                                                strokeWidth={2}
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<CustomTooltip formatValue={formatMZN} />} />
                                    <Legend
                                        verticalAlign="bottom"
                                        height={36}
                                        iconType="circle"
                                        iconSize={8}
                                        wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>

                <div className="dash-card">
                    <h3>💰 Contabilidade (IVA) ({ano})</h3>

                    {/* 🔥 Resumo IVA — agora com Liquidado, Dedutível, a Pagar/Receber */}
                    <div className="contab-resumo">
                        <div className="contab-item">
                            <span>Base Tributável (Vendas)</span>
                            <strong>{formatMZN(contab.baseTributavel)}</strong>
                        </div>
                        <div className="contab-item destaque">
                            <span>IVA Liquidado</span>
                            <strong>{formatMZN(contab.ivaLiquidado)}</strong>
                        </div>
                        <div className="contab-item">
                            <span>IVA Dedutível (Compras)</span>
                            <strong>{formatMZN(comprasContab.ivaDedutivel)}</strong>
                        </div>
                        <div className={`contab-item ${ivaAPagar >= 0 ? 'destaque-perigo' : 'destaque-ok'}`}>
                            <span>{ivaAPagar >= 0 ? 'IVA a Pagar' : 'IVA a Recuperar'}</span>
                            <strong>{formatMZN(Math.abs(ivaAPagar))}</strong>
                        </div>
                    </div>

                    <div className="tabela-wrapper">
                        <table className="tabela-dash">
                            <thead>
                                <tr>
                                    <th>Taxa</th>
                                    <th>Base</th>
                                    <th>IVA</th>
                                    <th>Faturas</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!temDadosContab && (
                                    <tr><td colSpan="4" className="vazio">Sem dados para {ano}</td></tr>
                                )}
                                {contab.ivaPorTaxa?.map((t, i) => (
                                    <tr key={i}>
                                        <td><strong>{t.taxa}%</strong></td>
                                        <td>{formatMZN(t.base)}</td>
                                        <td>{formatMZN(t.iva)}</td>
                                        <td>{t.count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* ===== 🔥 NOVO: PAINEL DE COMPRAS A FORNECEDOR ===== */}
            <section className="dash-card">
                <h3>🚚 Compras a Fornecedor ({ano})</h3>
                {!temDadosCompras ? (
                    <p className="vazio">Sem compras para {ano}</p>
                ) : (
                    <div className="tabela-wrapper">
                        <table className="tabela-dash">
                            <thead>
                                <tr>
                                    <th>Indicador</th>
                                    <th>Valor</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><strong>Total de Compras</strong></td>
                                    <td>{formatMZN(k.totalCompras)}</td>
                                </tr>
                                <tr>
                                    <td><strong>Pago a Fornecedores</strong></td>
                                    <td>{formatMZN(k.totalPagoCompras)}</td>
                                </tr>
                                <tr>
                                    <td><strong>A Pagar a Fornecedores</strong></td>
                                    <td>{formatMZN(k.totalAPagar)}</td>
                                </tr>
                                <tr>
                                    <td><strong>IVA Dedutível</strong></td>
                                    <td>{formatMZN(k.totalIvaDedutivel)}</td>
                                </tr>
                                <tr>
                                    <td><strong>Número de Compras</strong></td>
                                    <td>{formatNum(k.numeroCompras)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* ===== CLASSIFICACAO DE CLIENTES (CRM) ===== */}
            <section className="dash-card">
                <div className="dash-card-header">
                    <h3>🎖️ Classificação de Clientes (RFM)</h3>
                    <button className="btn-primary" onClick={carregarClassificacoes}>
                        {mostrarClassificacoes ? 'Atualizar' : 'Analisar Clientes'}
                    </button>
                </div>

                {mostrarClassificacoes && (
                    <div className="tabela-wrapper">
                        <table className="tabela-dash">
                            <thead>
                                <tr>
                                    <th>Cliente</th>
                                    <th>Nível Atual</th>
                                    <th>Nível Sugerido</th>
                                    <th>RFM</th>
                                    <th>Total Gasto</th>
                                    <th>Última Compra</th>
                                    <th>Ação</th>
                                </tr>
                            </thead>
                            <tbody>
                                {classificacoes.length === 0 && (
                                    <tr><td colSpan="7" className="vazio">Sem clientes</td></tr>
                                )}
                                {classificacoes.map(c => (
                                    <tr key={c._id} className={c.emRisco ? 'linha-risco' : ''}>
                                        <td>{c.nome}</td>
                                        <td>
                                            <span className={`badge badge-${(c.nivelAtual || '').toLowerCase()}`}>
                                                {c.nivelAtual}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`badge badge-${(c.nivelSugerido || '').toLowerCase()}`}>
                                                {c.nivelSugerido}
                                            </span>
                                        </td>
                                        <td>
                                            <small>R{c.rfm.r} F{c.rfm.f} M{c.rfm.m}</small>
                                        </td>
                                        <td>{formatMZN(c.metricas.totalGasto)}</td>
                                        <td>
                                            {c.metricas.diasDesdeUltimaCompra !== null
                                                ? `há ${c.metricas.diasDesdeUltimaCompra}d`
                                                : '—'}
                                        </td>
                                        <td>
                                            {c.divergente ? (
                                                <button
                                                    className="btn-mini"
                                                    onClick={() => aplicarNivel(c._id, c.nivelSugerido)}
                                                >
                                                    Aplicar
                                                </button>
                                            ) : (
                                                <span className="ok">✓</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

        </div>
    );
}